/**
 * Utility functions to test the i18n system
 * Use these in the browser console to verify language switching works
 */

/**
 * Get the current language from localStorage
 * Expected values: 'en' | 'ar'
 */
export function getCurrentLanguage(): string | null {
  return localStorage.getItem('i18nextLng');
}

/**
 * Get the current HTML direction attribute
 * Expected values: 'ltr' | 'rtl'
 */
export function getHTMLDirection(): string {
  return document.documentElement.dir;
}

/**
 * Get the current HTML lang attribute
 * Expected values: 'en' | 'ar'
 */
export function getHTMLLang(): string {
  return document.documentElement.lang;
}

/**
 * Get comprehensive i18n system status
 */
export function getI18nStatus() {
  return {
    localStorage: getCurrentLanguage(),
    htmlDirection: getHTMLDirection(),
    htmlLang: getHTMLLang(),
    timestamp: new Date().toISOString()
  };
}

/**
 * Test if language persists after simulated page refresh
 * This checks localStorage but doesn't actually reload the page
 */
export function testLanguagePersistence(): boolean {
  const currentLang = getCurrentLanguage();
  console.log('Current language in localStorage:', currentLang);
  console.log('HTML dir attribute:', getHTMLDirection());
  console.log('HTML lang attribute:', getHTMLLang());

  if (!currentLang) {
    console.warn('No language found in localStorage!');
    return false;
  }

  if (currentLang !== getHTMLLang()) {
    console.warn('Language mismatch: localStorage has', currentLang, 'but HTML lang is', getHTMLLang());
    return false;
  }

  const expectedDir = currentLang === 'ar' ? 'rtl' : 'ltr';
  if (getHTMLDirection() !== expectedDir) {
    console.warn('Direction mismatch: expected', expectedDir, 'but got', getHTMLDirection());
    return false;
  }

  console.log('✓ Language persistence test passed!');
  return true;
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).testI18n = {
    getCurrentLanguage,
    getHTMLDirection,
    getHTMLLang,
    getI18nStatus,
    testLanguagePersistence
  };

  console.log('%c[i18n Test Utils] Available in console as window.testI18n', 'color: #22c55e; font-weight: bold');
  console.log('Usage examples:');
  console.log('  - window.testI18n.getCurrentLanguage()');
  console.log('  - window.testI18n.getI18nStatus()');
  console.log('  - window.testI18n.testLanguagePersistence()');
}
